// src/pages/DashboardReportsPage/DashboardReportsPage.jsx
import React, { useState, useEffect } from "react";
import ReportWidget from "components/dashboard/ReportWidget";
import {
  fetchGuiasAtivosNoMes,
  fetchUpcomingPayments,
  fetchTotalExcursoesNoMes,
  fetchExcursoesConfirmadas,
  fetchAvailableYears,
} from "services/dashboardService";
import { Bell, Bus, Map, Loader2 } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "components/ui/card";
import ExcursionsChart from "components/dashboard/ExcursionsChart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/ui/tabs";
import { useUser } from "hooks/useUser";
import FinancialHistoryTab from "features/financeiro/FinancialHistoryTab";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const initialWidgetState = {
  value: 0,
  loading: true,
  error: null,
};

const initialPaymentsState = {
  data: [],
  loading: true,
  error: null,
};

const initialListState = {
  data: [],
  loading: true,
  error: null,
};

const DashboardReportsPage = () => {
  const { canViewFinanceiro, isAdmin } = useUser();
  const [guiasData, setGuiasData] = useState(initialListState);
  const [paymentsData, setPaymentsData] = useState(initialPaymentsState);
  const [excursoesData, setExcursoesData] = useState(initialWidgetState);
  const [proximasExcursoes, setProximasExcursoes] = useState(initialListState);

  const [guiasMonth, setGuiasMonth] = useState(new Date().getMonth());
  const [guiasYear, setGuiasYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);

  const mesAtualNome = new Date().toLocaleString("pt-BR", { month: "long" });
  const tituloExcursoesMes = `Excursões em ${mesAtualNome.charAt(0).toUpperCase() + mesAtualNome.slice(1)}`;

  const tituloGuiasMes = `Guias (${MESES[guiasMonth]} ${guiasYear})`;

  useEffect(() => {
    const loadWidgetData = async (fetcher, setter) => {
      try {
        const result = await fetcher();
        setter({ value: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar dados para o widget:", err);
        setter({ value: 0, loading: false, error: err.message });
      }
    };

    const loadPayments = async () => {
      try {
        const result = await fetchUpcomingPayments();
        setPaymentsData({ data: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar pagamentos pendentes:", err);
        setPaymentsData({ data: [], loading: false, error: err.message });
      }
    };

    const loadListData = async (fetcher, setter) => {
      try {
        const result = await fetcher();
        setter({ data: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar dados da lista:", err);
        setter({ data: [], loading: false, error: err.message });
      }
    };

    loadWidgetData(fetchTotalExcursoesNoMes, setExcursoesData);
    loadPayments();
    loadListData(fetchExcursoesConfirmadas, setProximasExcursoes);

    const loadYears = async () => {
      try {
        const years = await fetchAvailableYears();
        setAvailableYears(years);
      } catch (err) {
        console.error("Erro ao carregar anos disponíveis:", err);
      }
    };
    loadYears();
  }, []);

  useEffect(() => {
    const loadGuias = async () => {
      setGuiasData(prev => ({ ...prev, loading: true }));
      try {
        const result = await fetchGuiasAtivosNoMes(guiasMonth, guiasYear);
        setGuiasData({ data: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar guias:", err);
        setGuiasData({ data: [], loading: false, error: err.message });
      }
    };
    loadGuias();
  }, [guiasMonth, guiasYear]);

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto w-full">
      <div className="text-center mb-2">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-foreground">
          Resumo de Movimento
        </h1>
        <p className="text-muted-foreground mt-2">
          Uma visão geral e histórico do caixa.
        </p>
      </div>

      <Tabs defaultValue="geral" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="geral">Visão Geral</TabsTrigger>
          {(canViewFinanceiro || isAdmin) && (
            <TabsTrigger value="historico">Histórico de Caixa</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="geral" className="mt-0">
          <div className="columns-1 lg:columns-2 xl:columns-3 gap-6">
            <div className="break-inside-avoid mb-6">
              <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <div className="flex flex-col gap-2">
              <CardTitle className="text-base font-medium">{tituloGuiasMes}</CardTitle>
              <div className="flex gap-2">
                <Select value={guiasMonth.toString()} onValueChange={(val) => setGuiasMonth(parseInt(val))}>
                  <SelectTrigger className="h-8 w-[110px] text-xs">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((mes, i) => (
                      <SelectItem key={i} value={i.toString()}>{mes}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={guiasYear.toString()} onValueChange={(val) => setGuiasYear(parseInt(val))}>
                  <SelectTrigger className="h-8 w-[80px] text-xs">
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map((ano) => (
                      <SelectItem key={ano} value={ano.toString()}>{ano}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Map className="h-5 w-5 text-muted-foreground mt-1" />
          </CardHeader>
          <CardContent>
            {guiasData.loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : guiasData.error ? (
              <p className="text-sm text-destructive">Erro ao carregar</p>
            ) : guiasData.data.length === 0 ? (
              <div className="text-2xl font-bold">0</div>
            ) : (
              <div className="space-y-1 mt-2">
                {[...guiasData.data]
                  .sort((a, b) => b.count - a.count)
                  .map((guia, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center text-sm font-medium py-1.5 border-b last:border-0 border-gray-100 dark:border-border"
                  >
                    <span className="text-slate-700 dark:text-muted-foreground truncate pr-2" title={guia.name}>
                      {guia.name}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      guia.count > 0 
                        ? "bg-green-100 text-green-800 dark:bg-card/30 dark:text-muted-foreground" 
                        : "bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400"
                    }`}>
                      {guia.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
        <div className="break-inside-avoid mb-6">
          <ReportWidget
          title={tituloExcursoesMes}
          value={excursoesData.value}
          loading={excursoesData.loading}
          error={excursoesData.error}
          icon={Bus}
        />
        </div>

        <div className="break-inside-avoid mb-6">
          <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Pagamentos Pendentes
            </CardTitle>
            <CardDescription>
              Atrasados (vermelho), vencendo hoje (amarelo) e futuros (verde).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {paymentsData.loading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : paymentsData.error ? (
              <p className="text-sm text-destructive">
                Erro ao carregar dados.
              </p>
            ) : paymentsData.data.length > 0 ? (
              <ul className="flex flex-col gap-4">
                {paymentsData.data.map((payment) => {
                  const hoje = new Date();
                  hoje.setHours(0, 0, 0, 0);

                  const dueDate = new Date(payment.depositDueDate);
                  dueDate.setHours(0, 0, 0, 0);

                  let statusClasses = "";
                  if (dueDate < hoje) {
                    // Vencido / Atrasado
                    statusClasses =
                      "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
                  } else if (dueDate.getTime() === hoje.getTime()) {
                    // Vence Hoje
                    statusClasses =
                      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
                  } else {
                    // Vence no Futuro (Em dia)
                    statusClasses =
                      "bg-primary/15 text-primary dark:bg-primary/20";
                  }

                  return (
                    <li
                      key={payment.id}
                      className="flex flex-col border-b pb-2 last:border-b-0"
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">
                          {payment.guestInfo}
                        </span>
                        <span
                          className={`text-sm font-bold px-2 py-1 rounded-full ${statusClasses}`}
                        >
                          {payment.depositDueDate.toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        <span>{payment.phoneInfo || "Sem telefone"}</span> |{" "}
                        <span>Chalé: {payment.chaletNumber}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhum pagamento pendente encontrado.
              </p>
            )}
          </CardContent>
        </Card>
        </div>

        <div className="break-inside-avoid mb-6">
          <Card>
            <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bus className="h-5 w-5" />
              Próximas Datas com Excursões
            </CardTitle>
            <CardDescription>
              As próximas datas que possuem excursões confirmadas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {proximasExcursoes.loading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : proximasExcursoes.error ? (
              <p className="text-sm text-destructive">
                Erro ao carregar dados.
              </p>
            ) : proximasExcursoes.data.length > 0 ? (
              <ul className="flex flex-col gap-4">
                {proximasExcursoes.data.map((item) => (
                  <li
                    key={item.date.toISOString()}
                    className="flex justify-between items-center border-b pb-2 last:border-b-0"
                  >
                    <span className="font-semibold text-lg">
                      {item.date.toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "long",
                      })}
                    </span>
                    <span className="text-base">
                      <span className="font-bold text-2xl">{item.count}</span>
                      {item.count > 1 ? " excursões" : " excursão"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhuma excursão confirmada agendada.
              </p>
            )}
          </CardContent>
        </Card>
        </div>

        <div className="break-inside-avoid mb-6">
          <Card>
            <ExcursionsChart />
          </Card>
        </div>
      </div>
        </TabsContent>

        {(canViewFinanceiro || isAdmin) && (
          <TabsContent value="historico" className="mt-0">
            <FinancialHistoryTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default DashboardReportsPage;
